import {useEffect, useMemo, useState} from 'react';
import {
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import Icon from '@mui/material/Icon';
import {DragDropContext, Draggable, Droppable} from '@hello-pangea/dnd';
import axios from 'axios';
import {Confirm, Loading, Notify} from 'notiflix';
import {useSelector} from 'react-redux';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import {ENDPOINT} from 'constants';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';
import PriceOptionHintSettings from './PriceOptionHintSettings';
import OptionsEditor from './OptionsEditor';
import SortOptionsEditor from './SortOptionsEditor';
import PriceOptions from './PriceOptions';
import {
  DEFAULT_INDUSTRY_OPTIONS,
  FILTER_MODULES,
  FILTER_SOURCES,
  FILTER_TYPES,
  PLACEMENTS,
  SORT_OPTIONS,
  SOURCES_BY_TYPE,
  labelOf,
  slugify,
  typeNeedsOptions,
} from './filterConfig';

const EMPTY_FORM = {
  label: '',
  key: '',
  type: 'checkbox',
  source: 'custom',
  options: [],
  placement: 'sidebar',
  helpText: '',
  isActive: true,
  showInForm: true,
  requiredInForm: false,
};

const selectSx = {height: 44};

// Settings > Dynamic Filters. Admin picks a module (Value Packs) and a feature
// (Filters), then creates the filters shown on that module's storefront page:
// label, type, values for the type, and whether it sits in the sidebar or top bar.
const DynamicFilters = () => {
  const {user} = useSelector(state => state.auth);
  const canRead = usePermission('settings', 'read');
  const canUpdate = usePermission('settings', 'update');
  const authHeaders = {headers: {Authorization: `Bearer ${user?.accessToken}`}};
  const api = `${ENDPOINT}api/v1/admin/dynamic-filters`;

  const [module, setModule] = useState(FILTER_MODULES[0].value);
  const [feature, setFeature] = useState(FILTER_MODULES[0].features[0].value);
  const [filters, setFilters] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // filter being edited, null = new
  const [form, setForm] = useState(EMPTY_FORM);
  const [keyTouched, setKeyTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  const moduleConfig = FILTER_MODULES.find(m => m.value === module);

  const fetchFilters = async () => {
    try {
      Loading.circle('Loading filters...');
      const {data} = await axios.get(api, {...authHeaders, params: {module}});
      setFilters(data.data || []);
    } catch (error) {
      console.error(error);
      Notify.failure(
        error?.response?.data?.message || 'Failed to fetch filters',
      );
    } finally {
      Loading.remove();
    }
  };

  // Only modules with the Filters feature have filters to load (Products has
  // Price Options only)
  useEffect(() => {
    if (!canRead) {
      setFilters([]);
      return;
    }
    if (moduleConfig?.features.some(f => f.value === 'filters')) fetchFilters();
    else setFilters([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, canRead]);

  // Built-in sources can only be used once per module
  const usedSources = useMemo(
    () =>
      new Set(
        filters
          .filter(f => f.source !== 'custom' && f.id !== editing?.id)
          .map(f => f.source),
      ),
    [filters, editing],
  );

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setKeyTouched(false);
    setDialogOpen(true);
  };

  const openEdit = filter => {
    setEditing(filter);
    setForm({
      label: filter.label,
      key: filter.key,
      type: filter.type,
      source: filter.source,
      options: filter.options || [],
      placement: filter.placement,
      helpText: filter.helpText || '',
      isActive: filter.isActive,
      showInForm: filter.showInForm !== false,
      requiredInForm: Boolean(filter.requiredInForm),
    });
    setKeyTouched(true);
    setDialogOpen(true);
  };

  const setField = patch => setForm(prev => ({...prev, ...patch}));

  const handleLabel = label =>
    setField(keyTouched ? {label} : {label, key: slugify(label)});

  // Picking a type limits the data sources; pick the first allowed, unused one
  const handleType = type => {
    const allowed = SOURCES_BY_TYPE[type];
    const source = allowed.includes(form.source)
      ? form.source
      : allowed.find(s => s === 'custom' || !usedSources.has(s)) || allowed[0];
    applySource(source, type);
  };

  // Pre-fill options for built-in sources so the values match what packs store
  const applySource = (source, type = form.type) => {
    let {options} = form;
    if (source === 'sort' && !options.length) options = SORT_OPTIONS;
    if (source === 'industry' && !options.length)
      options = DEFAULT_INDUSTRY_OPTIONS;
    if (!typeNeedsOptions(type)) options = [];
    setField({type, source, options});
  };

  const validate = () => {
    if (!form.label.trim()) return 'Field label is required';
    if (!slugify(form.key)) return 'Key is required';
    if (!SOURCES_BY_TYPE[form.type].includes(form.source))
      return 'Pick a data source for this type';
    if (form.source !== 'custom' && usedSources.has(form.source)) {
      return `A "${labelOf(
        FILTER_SOURCES,
        form.source,
      )}" filter already exists`;
    }
    if (typeNeedsOptions(form.type)) {
      const options = form.options.filter(o => o.value && o.label);
      if (form.source !== 'category' && !options.length)
        return 'Add at least one option';
      if (options.length !== form.options.length)
        return 'Every option needs a label and a value';
      if (new Set(options.map(o => o.value)).size !== options.length)
        return 'Option values must be unique';
    }
    return null;
  };

  const handleSave = async () => {
    const error = validate();
    if (error) {
      Notify.warning(error);
      return;
    }
    const payload = {
      ...form,
      module,
      key: slugify(form.key),
      helpText: form.helpText.trim() || null,
    };
    try {
      setSaving(true);
      if (editing) {
        await axios.put(`${api}/${editing.id}`, payload, authHeaders);
        Notify.success('Filter updated');
      } else {
        await axios.post(api, payload, authHeaders);
        Notify.success('Filter created');
      }
      setDialogOpen(false);
      fetchFilters();
    } catch (err) {
      console.error(err);
      Notify.failure(err?.response?.data?.message || 'Failed to save filter');
    } finally {
      setSaving(false);
    }
  };

  // Quick inline updates from the list (active toggle, placement)
  const quickUpdate = async (filter, patch) => {
    const previous = filters;
    setFilters(list =>
      list.map(f => (f.id === filter.id ? {...f, ...patch} : f)),
    );
    try {
      await axios.put(`${api}/${filter.id}`, patch, authHeaders);
    } catch (err) {
      setFilters(previous);
      Notify.failure(err?.response?.data?.message || 'Failed to update filter');
    }
  };

  const handleDelete = filter => {
    Confirm.show(
      'Delete filter',
      `Delete "${filter.label}"? It will be removed from the storefront.`,
      'Delete',
      'Cancel',
      async () => {
        try {
          await axios.delete(
            `${api}/${filter.id}?label=${encodeURIComponent(filter.label)}`,
            authHeaders,
          );
          Notify.success('Filter deleted');
          fetchFilters();
        } catch (err) {
          Notify.failure(
            err?.response?.data?.message || 'Failed to delete filter',
          );
        }
      },
    );
  };

  const handleDragEnd = async ({source, destination}) => {
    if (!destination || destination.index === source.index) return;
    const previous = filters;
    const next = [...filters];
    const [moved] = next.splice(source.index, 1);
    next.splice(destination.index, 0, moved);
    setFilters(next);
    try {
      await axios.put(
        `${api}/reorder`,
        {ids: next.map(f => f.id)},
        authHeaders,
      );
    } catch (err) {
      setFilters(previous);
      Notify.failure(
        err?.response?.data?.message || 'Failed to reorder filters',
      );
    }
  };

  const sourceChoices = FILTER_SOURCES.filter(s =>
    SOURCES_BY_TYPE[form.type].includes(s.value),
  );

  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <NoPermissionNotice message="Your role doesn't include read access to dynamic filters. Ask an administrator to update your permissions." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3}>
        <Card>
          <MDBox p={3}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              flexWrap="wrap"
              gap={1}>
              <MDBox>
                <MDTypography variant="h5" fontWeight="medium">
                  Dynamic Filters
                </MDTypography>
                <MDTypography variant="button" color="text">
                  Choose a module, then manage the filters shoppers see on its
                  page.
                </MDTypography>
              </MDBox>
              {/* Note above the plan cards: same for every module */}
              <PriceOptionHintSettings />
            </MDBox>

            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Module"
                  value={module}
                  onChange={e => {
                    const next = FILTER_MODULES.find(
                      m => m.value === e.target.value,
                    );
                    setModule(next.value);
                    setFeature(next.features[0].value);
                  }}
                  SelectProps={{sx: selectSx}}>
                  {FILTER_MODULES.map(m => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Feature"
                  value={feature}
                  onChange={e => setFeature(e.target.value)}
                  SelectProps={{sx: selectSx}}>
                  {moduleConfig.features.map(f => (
                    <MenuItem key={f.value} value={f.value}>
                      {f.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </MDBox>
          <Divider sx={{my: 0}} />

          {feature === 'price_options' ? (
            <PriceOptions key={module} module={module} />
          ) : (
            <MDBox p={3}>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                flexWrap="wrap"
                gap={1}>
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium">
                    {moduleConfig.label} filters
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    Drag to change the order. Only active filters show on the
                    storefront.
                  </MDTypography>
                </MDBox>
                {canUpdate && (
                  <MDButton variant="gradient" color="info" onClick={openCreate}>
                    <Icon sx={{mr: 0.5}}>add</Icon> Add Filter
                  </MDButton>
                )}
              </MDBox>

              {filters.length === 0 ? (
                <MDBox
                  py={5}
                  textAlign="center"
                  sx={{
                    border: '1px dashed',
                    borderColor: 'grey.400',
                    borderRadius: 1,
                  }}>
                  <MDTypography variant="button" color="text">
                    No filters yet. The storefront page shows no filters until
                    you add some.
                  </MDTypography>
                </MDBox>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="dynamic-filters">
                    {dropProvided => (
                      <MDBox
                        ref={dropProvided.innerRef}
                        {...dropProvided.droppableProps}>
                        {filters.map((filter, index) => (
                          <Draggable
                            key={filter.id}
                            draggableId={filter.id}
                            index={index}>
                            {(dragProvided, snapshot) => (
                              <MDBox
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                display="flex"
                                alignItems="center"
                                gap={1.5}
                                p={1.5}
                                mb={1}
                                flexWrap="wrap"
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'grey.300',
                                  borderRadius: 1,
                                  backgroundColor: snapshot.isDragging
                                    ? 'grey.100'
                                    : 'white',
                                  opacity: filter.isActive ? 1 : 0.6,
                                }}>
                                <MDBox
                                  {...dragProvided.dragHandleProps}
                                  display="flex"
                                  sx={{cursor: 'grab'}}>
                                  <Icon color="action">drag_indicator</Icon>
                                </MDBox>

                                <MDBox flex="1 1 200px" minWidth={0}>
                                  <MDTypography
                                    variant="button"
                                    fontWeight="medium"
                                    display="block">
                                    {filter.label}
                                  </MDTypography>
                                  <MDTypography variant="caption" color="text">
                                    key: {filter.key}
                                    {filter.options?.length
                                      ? ` · ${filter.options.length} options`
                                      : ''}
                                  </MDTypography>
                                </MDBox>

                                <Chip
                                  size="small"
                                  label={
                                    labelOf(FILTER_TYPES, filter.type).split(
                                      ' (',
                                    )[0]
                                  }
                                />
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={
                                    filter.source === 'custom'
                                      ? 'Custom'
                                      : `Built-in: ${filter.source}`
                                  }
                                />
                                {filter.source === 'custom' && (
                                  <Tooltip
                                    title={
                                      filter.showInForm !== false
                                        ? 'This filter shows up as a field when you create or edit a value pack (Categories step), so you can tag packs with it'
                                        : 'This filter does not show up as a field when you create or edit a value pack'
                                    }>
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      color={
                                        filter.showInForm !== false
                                          ? 'success'
                                          : 'default'
                                      }
                                      label={
                                        filter.showInForm !== false
                                          ? 'Field in Value Pack form'
                                          : 'No field in Value Pack form'
                                      }
                                    />
                                  </Tooltip>
                                )}
                                {filter.source === 'custom' &&
                                  filter.showInForm !== false && (
                                    <Tooltip
                                      title={
                                        filter.requiredInForm
                                          ? 'A value pack cannot be saved without picking this field. Click to make it optional.'
                                          : 'This field can be left empty in the value pack form. Click to make it required.'
                                      }>
                                      <Chip
                                        size="small"
                                        clickable={canUpdate}
                                        variant={
                                          filter.requiredInForm
                                            ? 'filled'
                                            : 'outlined'
                                        }
                                        color={
                                          filter.requiredInForm
                                            ? 'error'
                                            : 'default'
                                        }
                                        label={
                                          filter.requiredInForm
                                            ? 'Required'
                                            : 'Optional'
                                        }
                                        onClick={
                                          canUpdate
                                            ? () =>
                                                quickUpdate(filter, {
                                                  requiredInForm:
                                                    !filter.requiredInForm,
                                                })
                                            : undefined
                                        }
                                      />
                                    </Tooltip>
                                  )}

                                <ToggleButtonGroup
                                  size="small"
                                  exclusive
                                  disabled={!canUpdate}
                                  value={filter.placement}
                                  onChange={(_, value) =>
                                    value &&
                                    quickUpdate(filter, {placement: value})
                                  }>
                                  {PLACEMENTS.map(p => (
                                    <ToggleButton
                                      key={p.value}
                                      value={p.value}
                                      sx={{px: 1.25, py: 0.25, fontSize: 12}}>
                                      {p.short}
                                    </ToggleButton>
                                  ))}
                                </ToggleButtonGroup>

                                <Tooltip
                                  title={
                                    filter.isActive
                                      ? 'Active — click to hide'
                                      : 'Hidden — click to show'
                                  }>
                                  <Switch
                                    checked={filter.isActive}
                                    disabled={!canUpdate}
                                    onChange={e =>
                                      quickUpdate(filter, {
                                        isActive: e.target.checked,
                                      })
                                    }
                                  />
                                </Tooltip>

                                {canUpdate && (
                                  <Tooltip title="Edit">
                                    <IconButton
                                      size="small"
                                      onClick={() => openEdit(filter)}>
                                      <Icon fontSize="small">edit</Icon>
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {canUpdate && (
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDelete(filter)}>
                                      <Icon fontSize="small">delete</Icon>
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </MDBox>
                            )}
                          </Draggable>
                        ))}
                        {dropProvided.placeholder}
                      </MDBox>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </MDBox>
          )}
        </Card>
      </MDBox>

      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        fullWidth
        maxWidth="md">
        <DialogTitle>{editing ? 'Edit filter' : 'Add filter'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth
                label="Field label"
                placeholder="e.g. Budget"
                value={form.label}
                onChange={e => handleLabel(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Key"
                value={form.key}
                disabled={!!editing && editing.source === 'custom'}
                helperText={
                  editing && editing.source === 'custom'
                    ? "Can't change — value packs are tagged with this key"
                    : 'Auto-generated from the label'
                }
                onChange={e => {
                  setKeyTouched(true);
                  setField({key: e.target.value});
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Type"
                value={form.type}
                onChange={e => handleType(e.target.value)}
                SelectProps={{sx: selectSx}}>
                {FILTER_TYPES.map(t => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Data source (what it matches)"
                value={form.source}
                onChange={e => applySource(e.target.value)}
                SelectProps={{sx: selectSx}}>
                {sourceChoices.map(s => (
                  <MenuItem
                    key={s.value}
                    value={s.value}
                    disabled={s.builtIn && usedSources.has(s.value)}>
                    {s.label}
                    {s.builtIn && usedSources.has(s.value)
                      ? ' — already added'
                      : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <MDTypography
                variant="button"
                fontWeight="medium"
                display="block"
                mb={1}>
                Values
              </MDTypography>
              {form.type === 'range' && (
                <MDTypography variant="caption" color="text">
                  The slider&apos;s min and max come from the value packs&apos;
                  prices — nothing to add here.
                </MDTypography>
              )}
              {form.type === 'sort' && (
                <SortOptionsEditor
                  options={form.options}
                  onChange={options => setField({options})}
                />
              )}
              {['checkbox', 'radio', 'dropdown'].includes(form.type) &&
                form.source === 'category' && (
                  <OptionsEditor
                    options={form.options}
                    onChange={options => setField({options})}
                    helperText="Leave empty to list every category. Otherwise use the category name as the value."
                  />
                )}
              {['checkbox', 'radio', 'dropdown'].includes(form.type) &&
                form.source === 'industry' && (
                  <OptionsEditor
                    options={form.options}
                    onChange={options => setField({options})}
                    helperText="Matched against the industries already saved on each value pack. To tag packs with industries in the value pack form, use a custom filter."
                  />
                )}
              {['checkbox', 'radio', 'dropdown'].includes(form.type) &&
                form.source === 'custom' && (
                  <OptionsEditor
                    options={form.options}
                    onChange={options => setField({options})}
                    helperText="Each value pack is tagged with these values in the value pack form (Categories step)."
                  />
                )}
            </Grid>

            <Grid item xs={12} md={8}>
              <MDTypography
                variant="button"
                fontWeight="medium"
                display="block"
                mb={1}>
                Placement
              </MDTypography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={form.placement}
                onChange={(_, value) => value && setField({placement: value})}>
                {PLACEMENTS.map(p => (
                  <ToggleButton key={p.value} value={p.value} sx={{px: 1.5}}>
                    {p.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <MDTypography
                variant="caption"
                color="text"
                display="block"
                mt={0.5}>
                Left / Right show as a filter panel beside the cards; Top /
                Bottom as a row of dropdown buttons above / below them. On
                mobile every filter opens in the &quot;Show Filters&quot; sheet.
              </MDTypography>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                sx={{mt: 3}}
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={e => setField({isActive: e.target.checked})}
                  />
                }
                label="Active (show on storefront)"
              />
            </Grid>

            {form.source === 'custom' && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.showInForm}
                      onChange={e => setField({showInForm: e.target.checked})}
                    />
                  }
                  label="Show this field in the value pack create / edit form"
                />
                {form.showInForm && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.requiredInForm}
                        onChange={e =>
                          setField({requiredInForm: e.target.checked})
                        }
                      />
                    }
                    label="Required in the value pack form"
                  />
                )}
                <MDTypography variant="caption" color="text" display="block">
                  The field label becomes the field&apos;s title in the form,
                  the help text its description, and the options its choices.
                  Only packs tagged here match this filter on the storefront.
                </MDTypography>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Help text (tooltip, optional)"
                value={form.helpText}
                inputProps={{maxLength: 255}}
                onChange={e => setField({helpText: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="secondary"
            disabled={saving}
            onClick={() => setDialogOpen(false)}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            disabled={saving}
            onClick={handleSave}>
            {saving ? 'Saving...' : editing ? 'Save changes' : 'Create filter'}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default DynamicFilters;
